/**
 * Review Questionnaire - Dialog components for approve, reject, and request more info actions.
 */
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  CheckCircle2,
  XCircle,
  MessageSquare,
  Loader2,
} from 'lucide-react';
import type { ApprovalConfidence } from '../../lib/api';

/* ------------------------------------------------------------------ */
/*  Approve Dialog                                                     */
/* ------------------------------------------------------------------ */

interface ApproveDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  approvalConfidence: ApprovalConfidence;
  onConfidenceChange: (confidence: ApprovalConfidence) => void;
  onApprove: () => void;
  actionLoading: boolean;
}

export function ApproveDialog({
  open,
  onOpenChange,
  approvalConfidence,
  onConfidenceChange,
  onApprove,
  actionLoading,
}: ApproveDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Approve Questionnaire?</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to approve this questionnaire? Please indicate your approval confidence level.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4 space-y-3">
          <Label className="text-sm font-medium">Approval Confidence</Label>
          <div className="space-y-2">
            <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="radio"
                name="approvalConfidence"
                value="no_issues"
                checked={approvalConfidence === 'no_issues'}
                onChange={() => onConfidenceChange('no_issues')}
                className="h-4 w-4 text-green-600"
              />
              <div>
                <div className="font-medium text-green-700">No Issues</div>
                <div className="text-sm text-muted-foreground">
                  The questionnaire responses meet all requirements without concerns
                </div>
              </div>
            </label>
            <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors">
              <input
                type="radio"
                name="approvalConfidence"
                value="with_concerns"
                checked={approvalConfidence === 'with_concerns'}
                onChange={() => onConfidenceChange('with_concerns')}
                className="h-4 w-4 text-amber-600"
              />
              <div>
                <div className="font-medium text-amber-700">With Concerns</div>
                <div className="text-sm text-muted-foreground">
                  Approved but flagged for monitoring due to some concerns
                </div>
              </div>
            </label>
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onConfidenceChange('no_issues')}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onApprove}
            disabled={actionLoading}
            className={approvalConfidence === 'with_concerns' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}
          >
            {actionLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <CheckCircle2 className="h-4 w-4 mr-2" />
            )}
            Approve {approvalConfidence === 'with_concerns' ? '(With Concerns)' : ''}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Reject Dialog                                                      */
/* ------------------------------------------------------------------ */

interface RejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feedback: string;
  onFeedbackChange: (feedback: string) => void;
  onReject: () => void;
  actionLoading: boolean;
}

export function RejectDialog({
  open,
  onOpenChange,
  feedback,
  onFeedbackChange,
  onReject,
  actionLoading,
}: RejectDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Reject Questionnaire?</AlertDialogTitle>
          <AlertDialogDescription>
            Please provide a reason for rejecting this questionnaire.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4">
          <Textarea
            placeholder="Enter rejection reason..."
            value={feedback}
            onChange={(e) => onFeedbackChange(e.target.value)}
            rows={3}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onFeedbackChange('')}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onReject}
            disabled={actionLoading}
            className="bg-red-600 hover:bg-red-700"
          >
            {actionLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <XCircle className="h-4 w-4 mr-2" />
            )}
            Reject
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/* ------------------------------------------------------------------ */
/*  Request More Info Dialog                                            */
/* ------------------------------------------------------------------ */

interface MoreInfoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feedback: string;
  onFeedbackChange: (feedback: string) => void;
  onRequestMoreInfo: () => void;
  actionLoading: boolean;
}

export function MoreInfoDialog({
  open,
  onOpenChange,
  feedback,
  onFeedbackChange,
  onRequestMoreInfo,
  actionLoading,
}: MoreInfoDialogProps) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Request More Information</AlertDialogTitle>
          <AlertDialogDescription>
            Describe what additional information you need from the supplier.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4">
          <Textarea
            placeholder="Enter your request..."
            value={feedback}
            onChange={(e) => onFeedbackChange(e.target.value)}
            rows={3}
          />
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onFeedbackChange('')}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={onRequestMoreInfo}
            disabled={actionLoading || !feedback.trim()}
          >
            {actionLoading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <MessageSquare className="h-4 w-4 mr-2" />
            )}
            Send Request
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
